/**
 * Damaged returns (Eric, 2026-11-02): a check-in is the inspection, so a returned trailer
 * goes back to Available at once. When the check-in notes damage, the person checking in
 * chooses ("let user choose what to do"); this card is the answer pre-selected for them, and
 * what happens when no one chooses (older Pocket builds, a customer's check-in): back to
 * Available (the default; the damage still goes to Records → Damage for review), or hold it
 * on a work order until the work order is completed or cancelled.
 *
 * Lives in the Pocket > Setup tab next to the check-in photo limits. The check-in trigger
 * reads the same pocket_settings key, so a change applies to the next return.
 */

import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'react-toastify';
import { useRBAC } from '@/contexts/RBACContext';
import { getDamagedReturns, updateDamagedReturns } from '@/lib/pocketSettingsApi';
import { cn } from '@/lib/utils';

const OPTIONS = [
  {
    hold: false,
    label: 'Back to Available',
    detail: 'The trailer is available at once. The damage still goes to Records → Damage for review.',
  },
  {
    hold: true,
    label: 'Hold for repair',
    detail: 'Opens a work order. The trailer reads Needs attention until the work order is completed or cancelled.',
  },
] as const;

export function DamagedReturnsCard() {
  const { isSuperadmin } = useRBAC();
  const [hold, setHold] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getDamagedReturns()
      .then((data) => {
        if (!cancelled) setHold(data.hold);
      })
      .catch(() => {
        // Non-fatal: the card stays on "Loading…".
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function choose(next: boolean) {
    if (!isSuperadmin || saving || hold === null || next === hold) return;
    const previous = hold;
    setHold(next);
    setSaving(true);
    try {
      const saved = await updateDamagedReturns({ hold: next });
      setHold(saved.hold);
      toast.success(saved.hold ? 'Damaged returns will be held for repair.' : 'Damaged returns will go back to Available.');
    } catch {
      setHold(previous);
      toast.error('Save failed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold">Damaged returns</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          A check-in is the inspection: a returned trailer goes back to Available. When the check-in notes damage,
          the person checking in chooses. This is the answer pre-selected for them, and what happens when no one
          chooses.
        </p>
        {hold === null ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <div role="radiogroup" aria-label="Damaged returns" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {OPTIONS.map((option) => {
              const checked = hold === option.hold;
              return (
                <button
                  key={option.label}
                  type="button"
                  role="radio"
                  aria-checked={checked}
                  aria-label={option.label}
                  disabled={!isSuperadmin || saving}
                  onClick={() => choose(option.hold)}
                  className={cn(
                    'rounded-md border p-4 text-left transition-colors',
                    checked ? 'border-primary bg-primary/10' : 'border-border/50 bg-card hover:border-border',
                    (!isSuperadmin || saving) && 'cursor-default',
                  )}
                >
                  <div className="text-sm font-medium">{option.label}</div>
                  <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{option.detail}</div>
                </button>
              );
            })}
          </div>
        )}
        {!isSuperadmin && hold !== null ? (
          <p className="text-xs text-muted-foreground mt-3">Only a superadmin can change this.</p>
        ) : null}
      </CardContent>
    </Card>
  );
}
